/**
 * 格式化工具函数
 */
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * 格式化数字为货币格式
 * @param amount 金额
 * @param currency 货币符号，默认为 ¥
 * @param locale 地区设置，默认为 zh-CN
 * @returns 格式化后的货币字符串
 */
export function formatCurrency(amount: number, currency: string = "¥", locale: string = "zh-CN"): string {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency === "¥" ? "CNY" : "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  if (currency === "¥") {
    return formatter.format(amount)
  }

  return `${currency}${amount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @param decimals 小数位数，默认为 2
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
}

// 格式化时间 yyyy-MM-dd hh:mm:ss
export function formatTimeCN(time: string | number | Date, fmt: string) {
  if (!time) {
    return ""
  }
  time = new Date(time)
  const o = {
    "M+": time.getMonth() + 1,
    "d+": time.getDate(),
    "h+": time.getHours(),
    "m+": time.getMinutes(),
    "s+": time.getSeconds(),
    "q+": Math.floor((time.getMonth() + 3) / 3),
    S: time.getMilliseconds(),
  } as const
  if (/(y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, `${time.getFullYear()}`.substr(4 - RegExp.$1.length))
  }
  const keys = Object.keys(o) as any[]
  for (const k of keys) {
    type OKeys = keyof typeof o
    for (const k of keys as OKeys[]) {
      const reg = new RegExp(`(${k})`)
      if (reg.test(fmt)) {
        const val = String(o[k])
        fmt = fmt.replace(RegExp.$1, RegExp.$1.length === 1 ? val : `00${val}`.substring(val.length))
      }
    }
    return fmt
  }
}

type InputTime = string | number | Date

export type FormatMeetingPeriodOptions = {
  withTime?: boolean
  timeZoneIana?: string
  timeZoneLabel?: string
  locale?: string
}

function toDayjsTz(value: InputTime, tz?: string) {
  let d: dayjs.Dayjs
  if (typeof value === "number") {
    d = value < 1e12 ? dayjs.unix(value) : dayjs(value)
  } else {
    d = dayjs(value)
  }
  if (tz) {
    const ms = d.valueOf()
    return dayjs.utc(ms).tz(tz)
  }
  return d
}

export function formatMeetingPeriod(start: InputTime, end?: InputTime, opts: FormatMeetingPeriodOptions = {}): string {
  const { withTime = false, timeZoneIana = "Asia/Shanghai", timeZoneLabel = "GMT+8", locale = "en" } = opts

  const sd = toDayjsTz(start, timeZoneIana).locale(locale)
  const ed = end ? toDayjsTz(end, timeZoneIana).locale(locale) : undefined

  if (!end) {
    if (withTime) {
      return `${sd.format("MMM D, HH:mm")} (${timeZoneLabel})`
    }
    return `${sd.format("MMM D, YYYY")} (${timeZoneLabel})`
  }

  const sameYear = sd.year() === ed!.year()
  const sameMonth = sd.month() === ed!.month() && sameYear
  const sameDay = sameMonth && sd.date() === ed!.date()

  if (withTime) {
    if (sameDay) {
      return `${sd.format("MMM D, HH:mm")} - ${ed!.format("HH:mm")} (${timeZoneLabel})`
    }
    if (sameYear) {
      return `${sd.format("MMM D, HH:mm")} - ${ed!.format("MMM D, HH:mm")} (${timeZoneLabel})`
    }
    return `${sd.format("MMM D, YYYY, HH:mm")} - ${ed!.format("MMM D, YYYY, HH:mm")} (${timeZoneLabel})`
  }

  if (sameDay) {
    return `${sd.format("MMM D, YYYY")} (${timeZoneLabel})`
  }
  if (sameYear) {
    if (sameMonth) {
      return `${sd.format("MMM D")} - ${ed!.format("D")}, ${sd.format("YYYY")} (${timeZoneLabel})`
    }
    return `${sd.format("MMM D")} - ${ed!.format("MMM D")}, ${sd.format("YYYY")} (${timeZoneLabel})`
  }
  return `${sd.format("MMM D, YYYY")} - ${ed!.format("MMM D, YYYY")} (${timeZoneLabel})`
}
